const CLIENT_ID = "f8f1713b777347b98ca3934762b892b9";
const REDIRECT_URI = "https://127.0.0.1:3000/callback";
const AUTH_ENDPOINT = "https://accounts.spotify.com/authorize";
const TOKEN_ENDPOINT = "https://accounts.spotify.com/api/token";

const SCOPES = [
  "user-read-currently-playing",
  "user-read-playback-state",
];

function randomString(len = 64) {
  const chars = "ABCDEFGHIJKLMNOPQRSTUVWXYZabcdefghijklmnopqrstuvwxyz0123456789-._~";
  const bytes = crypto.getRandomValues(new Uint8Array(len));
  return Array.from(bytes, (b) => chars[b % chars.length]).join("");
}

function base64UrlEncode(bytes) {
  return btoa(String.fromCharCode(...new Uint8Array(bytes)))
    .replace(/\+/g, "-")
    .replace(/\//g, "_")
    .replace(/=+$/g, "");
}

async function sha256(str) {
  const data = new TextEncoder().encode(str);
  return crypto.subtle.digest("SHA-256", data);
}

async function createPkcePair() {
  const code_verifier = randomString(64);
  const digest = await sha256(code_verifier);
  const code_challenge = base64UrlEncode(digest);
  return { code_verifier, code_challenge };
}

export async function startSpotifyLogin() {
  const state = randomString(32);
  const { code_verifier, code_challenge } = await createPkcePair();

  sessionStorage.setItem("sp_state", state);
  sessionStorage.setItem("sp_code_verifier", code_verifier);

  const params = new URLSearchParams({
    client_id: CLIENT_ID,
    response_type: "code",
    redirect_uri: REDIRECT_URI,
    scope: SCOPES.join(" "),
    state,
    code_challenge_method: "S256",
    code_challenge,
  });

  const authUrl = `${AUTH_ENDPOINT}?${params.toString()}`;

  window.location.assign(authUrl);
}


export async function exchangeCodeForToken(code, returnedState) {
  const expectedState = sessionStorage.getItem("sp_state");
  const code_verifier = sessionStorage.getItem("sp_code_verifier");
  if (!expectedState || returnedState !== expectedState) throw new Error("Invalid state");
  if (!code_verifier) throw new Error("Missing code_verifier");

  const body = new URLSearchParams({
    client_id: CLIENT_ID,
    grant_type: "authorization_code",
    code,
    redirect_uri: REDIRECT_URI,
    code_verifier,
  });

  const res = await fetch(TOKEN_ENDPOINT, {
    method: "POST",
    headers: { "Content-Type": "application/x-www-form-urlencoded" },
    body,
  });

  if (!res.ok) {
    const txt = await res.text();
    throw new Error(`Token exchange failed: ${res.status} ${txt}`);
  }

  const token = await res.json();

  localStorage.setItem(
    "sp_token",
    JSON.stringify({
      ...token,
      obtained_at: Date.now(),
    })
  );

  sessionStorage.removeItem("sp_state");
  sessionStorage.removeItem("sp_code_verifier");

  return token;
}

export function getStoredToken() {
  const raw = localStorage.getItem("sp_token");
  return raw ? JSON.parse(raw) : null;
}

export function isTokenExpired(stored, skewMs = 30_000) {
  if (!stored?.expires_in || !stored?.obtained_at) return true;
  return Date.now() > stored.obtained_at + stored.expires_in * 1000 - skewMs;
}

export async function refreshAccessToken() {
  const stored = getStoredToken();
  if (!stored?.refresh_token) throw new Error("No refresh_token stored");

  const body = new URLSearchParams({
    client_id: CLIENT_ID,
    grant_type: "refresh_token",
    refresh_token: stored.refresh_token,
  });

  const res = await fetch(TOKEN_ENDPOINT, {
    method: "POST",
    headers: { "Content-Type": "application/x-www-form-urlencoded" },
    body,
  });

  if (!res.ok) {
    const txt = await res.text();
    throw new Error(`Refresh failed: ${res.status} ${txt}`);
  }

  const fresh = await res.json();

  const merged = {
    ...stored,
    ...fresh,
    refresh_token: fresh.refresh_token ?? stored.refresh_token,
    obtained_at: Date.now(),
  };

  localStorage.setItem("sp_token", JSON.stringify(merged));
  return merged;
}
